<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    @open="beforeOpen"
    @opened="opened"
    @closed="closed"
    @close="$emit('close')"
  >
    <h1 class="tw-mb-8">
      {{ $t('foreigners.basic_info.family_dialog.new_member') }}
    </h1>
    <el-form
      ref="Form"
      :model="form"
      :rules="formRules"
      label-position="top"
      :disabled="isLoading"
      class="tw-gap-2 tw-grid md:tw-grid-cols-2 tw-mt-3"
      @submit.native.prevent="submitForm"
    >
      <template v-if="isCreateNew">
        <el-form-item
          :label="$t('common.fields.first_name')"
          prop="new_user.first_name"
        >
          <el-input
            v-model="form.new_user.first_name"
          />
        </el-form-item>
        <el-form-item
          :label="$t('common.fields.last_name')"
          prop="new_user.last_name"
        >
          <el-input
            v-model="form.new_user.last_name"
          />
        </el-form-item>
        <el-form-item
          :label="$t('common.fields.citizenship')"
          prop="new_user.citizenship_id"
        >
          <el-select
            v-model="form.new_user.citizenship_id"
            placeholder=""
            filterable
          >
            <el-option
              v-for="item in countries"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
      </template>
      <el-form-item
        v-if="!isCreateNew"
        :label="$t('common.fields.foreigner')"
        prop="relation_with_id"
      >
        <el-select
          v-model="form.relation_with_id"
          placeholder=""
          filterable
        >
          <el-option
            v-for="item in foreignersOptions"
            :key="item.id"
            :label="item.fullname"
            :value="item.id"
          />
        </el-select>
      </el-form-item>

      <el-form-item
        :label="$t('common.fields.relation_type')"
        prop="relation_type"
      >
        <el-select
          v-model="form.relation_type"
          placeholder=""
          filterable
        >
          <el-option
            v-for="item in familyRelations"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
    </el-form>
    <template #footer>
      <div class="tw-flex tw-justify-end tw-gap-2 flex-col md:flex-row">
        <i-button
          class="tw-w-40"
          @click="$emit('close')"
        >
          {{ $t('common.actions.cancel') }}
        </i-button>
        <i-button
          type="primary"
          class="tw-w-40"
          :is-loading="isLoading"
          @click="submitForm"
        >
          {{ $t('common.actions.save') }}
        </i-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import _cloneDeep from 'lodash/cloneDeep';
import _get from 'lodash/get';

const FORM_BASE = {
  relation_with_id: null,
  relation_type: null,
  new_user: {
    first_name: '',
    last_name: '',
    citizenship_id: '',
  },
};

export default {
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    isCreateNew: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,
      form: _cloneDeep(FORM_BASE),
      formRules: {},
    };
  },

  computed: {
    ...mapState({
      familyRelations: (state) => state.v2.shared.enums.enums.family_relations,
      foreigner: (state) => state.v2.admin.foreigners.foreigner.foreigner,
      countries: (state) => state.v2.shared.enums.enums.countries,
      foreignersOptions: (state) => state.v2.admin.foreigners.foreigners.allForeignersSimple,
    }),
  },

  methods: {
    beforeOpen() {
      this.formRules = {
        relation_with_id: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
        relation_type: [
          { required: true, message: 'Required', trigger: 'blur' },
        ],
      };

      if (this.isCreateNew) {
        this.formRules['new_user.first_name'] = [{ required: true, message: 'Required', trigger: 'blur' }];
        this.formRules['new_user.last_name'] = [{ required: true, message: 'Required', trigger: 'blur' }];
      }
    },
    opened() {
      this.$store.dispatch('v2/admin/foreigners/foreigners/loadAllForeignersSimple');
      this.$refs.Form?.clearValidate();
    },
    closed() {
      this.form = _cloneDeep(FORM_BASE);
      this.$refs.Form?.clearValidate();
    },

    async submitForm() {
      const isValid = await this.$refs.Form.validate().catch(() => false);
      if (!isValid) {
        return;
      }

      this.isLoading = true;

      try {
        const data = {};

        if (this.isCreateNew) {
          data.relation_type = this.form.relation_type;
          data.new_user = this.form.new_user;
        } else {
          data.relation_with_id = this.form.relation_with_id;
          data.relation_type = this.form.relation_type;
        }

        const response = await this.$store.dispatch('foreigners/createForeignerFamily', {
          id: this.foreigner.id,
          family: [data],
        });

        const message = _get(response, 'data.message');
        this.$message.success({
          message: this.$capitalize(message || 'Success'),
          showClose: true,
        });

        this.$emit('refresh');
        this.$emit('close');
      } catch(error) {
        const message = _get(error, 'response.data.data.relation_with_id');
        this.$message.error({
          message: this.$capitalize(message || 'Error'),
          showClose: true,
        });
      }

      this.isLoading = false;
    },
  },
};
</script>
